<template>
  <div class="mention-text">
    <template v-for="(seg, i) in segments" :key="i">
      <span
        v-if="seg.type === 'mention' && isPendingLocationMention(seg)"
        data-testid="location-mention"
        :data-location-id="seg.id"
        data-saving="true"
        :class="[mentionLinkClass('location', false), PENDING_LOCATION_MENTION_CLASS]"
        title="Locatie wordt opgeslagen…"
      >
        {{ getPendingLocationBaseLabel(seg.id) }}
        <span
          class="mention-location-pending-spinner"
          aria-hidden="true"
          v-html="PENDING_LOCATION_SPINNER_SVG"
        />
      </span>
      <component
        v-else-if="seg.type === 'mention'"
        :is="enableLinks ? 'a' : 'span'"
        :data-testid="mentionTestId(seg.kind)"
        :data-location-id="seg.kind === 'location' ? seg.id : undefined"
        :data-file-id="seg.kind === 'file' ? seg.id : undefined"
        :data-person-id="seg.kind === 'person' ? seg.id : undefined"
        :data-project-id="seg.kind === 'project' ? seg.id : undefined"
        :data-business-id="seg.kind === 'business' ? seg.id : undefined"
        :role="enableLinks ? 'button' : undefined"
        :tabindex="enableLinks ? 0 : undefined"
        :class="mentionLinkClass(seg.kind, enableLinks)"
        :title="mentionTitle(seg.kind, seg.id)"
        @click="onMentionClick(seg, $event)"
        @dblclick="onMentionDblClick(seg, $event)"
        @keydown.enter="onMentionClick(seg, $event)"
      >
        {{ displayLabel(seg.kind, seg.id) }}
      </component>
      <span v-else v-html="renderText(seg.value)" />
    </template>

    <LocationMapModal
      v-if="mapLocation"
      :inline="usesInlineLocationMap()"
      :location="mapLocation"
      @saved="onLocationSaved"
      @close="closeMap"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, watch } from "vue"
import LocationMapModal from "@/components/mention/LocationMapModal.vue"
import { convertLinks } from "@/helper/message-helper"
import { Bus } from "@/services/bus"
import {
  useMention,
  getPendingLocationBaseLabel,
  isPendingLocationId,
  mentionTestId,
  PENDING_LOCATION_MENTION_CLASS,
  PENDING_LOCATION_SPINNER_SVG
} from "@/composables/mention/use-mention"

const props = withDefaults(
  defineProps<{
    text?: string | null
    enableLinks?: boolean
    preferLocationAddress?: boolean
    locationNameWithAddress?: boolean
    useLocationDetailModal?: boolean
  }>(),
  {
    text: "",
    enableLinks: true,
    preferLocationAddress: false,
    locationNameWithAddress: false,
    useLocationDetailModal: false
  }
)

const {
  parseBody,
  mentionLinkClass,
  labelFor,
  mentionTitle,
  ensureLoaded,
  mapLocation,
  openMention,
  handleLocationMentionClick,
  handleLocationMentionDoubleClick,
  closeMap,
  refreshOpenMapLocation,
  usesInlineLocationMap,
  locationLabelsRevision
} = useMention({
  preferLocationAddress: props.preferLocationAddress,
  locationNameWithAddress: props.locationNameWithAddress,
  useLocationDetailModal: props.useLocationDetailModal
})

const segments = computed(() => parseBody(props.text ?? ""))

function displayLabel(kind: Parameters<typeof labelFor>[0], id: string): string {
  void locationLabelsRevision.value
  return labelFor(kind, id)
}

function onLocationSaved() {
  closeMap()
}

function onInstructionTextChanged() {
  void refreshOpenMapLocation()
}

onMounted(() => Bus.on("instruction-text-changed", onInstructionTextChanged))
onBeforeUnmount(() => Bus.off("instruction-text-changed", onInstructionTextChanged))

watch(
  segments,
  (segs) =>
    segs.forEach((s) => s.type === "mention" && s.kind === "location" && ensureLoaded(s.id)),
  { immediate: true }
)

function isPendingLocationMention(seg: { type: string; kind?: string; id?: string }) {
  return seg.type === "mention" && seg.kind === "location" && isPendingLocationId(seg.id!)
}

function renderText(value: string): string {
  return props.enableLinks ? convertLinks(value) : value
}

function onMentionClick(
  seg: { kind: "location" | "file" | "person" | "project" | "business"; id: string },
  event: Event
) {
  if (!props.enableLinks) return
  event.stopPropagation()
  event.preventDefault()
  if (seg.kind === "location") {
    handleLocationMentionClick(seg.id, event)
    return
  }
  openMention(seg.kind, seg.id, event)
}

function onMentionDblClick(
  seg: { kind: "location" | "file" | "person" | "project" | "business"; id: string },
  event: Event
) {
  if (!props.enableLinks || seg.kind !== "location") return
  handleLocationMentionDoubleClick(seg.id, event)
}
</script>
